import numeral from 'numeral';
import { identity } from 'lodash-es';
import moment from 'moment';
import { TypeDefinition } from './common.type';
import { formatDate, formatDateTime } from './utils/date.util';
import { precisionRound } from './utils/formatters.util';

numeral.register('locale', 'lunar', {
    delimiters: {
        thousands: ' ',
        decimal: '.',
    },
    abbreviations: {
        thousand: 'K',
        million: 'M',
        billion: 'B',
        trillion: 'T',
    },
    currency: {
        symbol: '€',
    },
    ordinal: () => '',
});

numeral.locale('lunar');
numeral.nullFormat('-');

const DATE_TIME: TypeDefinition = {
    typeId: 'DATE_TIME',
    parse: identity,
    parseForExport: identity,
    format: (val: any) => (val != null ? formatDateTime(val) : '-'),
    formatTitle: identity,
};

const DATE: TypeDefinition = {
    typeId: 'DATE',
    parse: identity,
    parseForExport: identity,
    format: (val: any) => (val != null ? formatDate(val) : '-'),
    formatTitle: identity,
};

const TEXT: TypeDefinition = {
    typeId: 'TEXT',
    parse: identity,
    parseForExport: identity,
    format: (val: any, options: any) => {
        const { fallbackValue = '-' } = options || {};
        return val != null ? val : fallbackValue;
    },
    formatTitle: identity,
};

const ID: TypeDefinition = {
    typeId: 'ID',
    parse: identity,
    parseForExport: identity,
    format: (val: any) => (val != null ? val : '-'),
    formatTitle: identity,
};

const INT: TypeDefinition = {
    typeId: 'INT',
    parse: (val: any) => val && numeral(val).format('0'),
    parseForExport: identity,
    format: (val: any, options: any) => {
        const { useMetricPrefix = false } = options || {};
        return val != null ? numeral(INT.parse(val)).format(`0,0${useMetricPrefix ? 'a' : ''}`) : '-';
    },
    formatTitle: identity,
};

const FLOAT: TypeDefinition = {
    typeId: 'FLOAT',
    parse: (val: any) => val && numeral(val).format('0.00'),
    parseForExport: (val: any) => val && precisionRound(val, 2),
    format: (val: any, options: any) => {
        const { showFraction = true } = options || {};
        return val != null ? numeral(FLOAT.parse(val)).format(`0,0${showFraction ? '.00' : ''}`) : '-';
    },
    formatTitle: identity,
};

const RATIO: TypeDefinition = {
    typeId: 'RATIO',
    parse: (val: any) => val && numeral(val).format('0.00'),
    parseForExport: (val: any) => val && precisionRound(val, 2),
    format: (val: any) => (val != null ? numeral(RATIO.parse(val)).format('0,0.00') : '-'),
    formatTitle: identity,
};

const EUR: TypeDefinition = {
    typeId: 'EUR',
    parse: (val: any) => val && numeral(val).format('0.00'),
    parseForExport: (val: any) => val && precisionRound(val, 2),
    format: (val: any, options: any) => {
        const { useCurrencySymbol = false, showFraction = true, useMetricPrefix = false } = options || {};
        return val != null
            ? numeral(EUR.parse(val)).format(
                  `0,0${useMetricPrefix ? 'a' : ''}${showFraction ? '.00' : ''}${
                      useCurrencySymbol ? ' $' : ''
                  }`
              )
            : '-';
    },
    formatTitle: (title: string, options: any) => {
        const { showUnits = true } = options || {};
        return `${title}${showUnits ? ', EUR' : ''}`;
    },
};

// Alias for EUR when you need format in table with currency
const EUR_SYMBOL: TypeDefinition = {
    ...EUR,
    typeId: 'EUR_SYMBOL',
    format: val => EUR.format(val, { useCurrencySymbol: true }),
};

const EUR4: TypeDefinition = {
    typeId: 'EUR4',
    parse: (val: any) => val && numeral(val).format('0.0000'),
    parseForExport: (val: any) => val && precisionRound(val, 4),
    format: (val: any, options: any) => {
        const { useCurrencySymbol = false } = options || {};
        return val != null ? numeral(val).format(`0,0.0000${useCurrencySymbol ? ' $' : ''}`) : '-';
    },
    formatTitle: (title: string, options: any) => {
        const { showUnits = true } = options || {};
        return `${title}${showUnits ? ', EUR' : ''}`;
    },
};

const P100: TypeDefinition = {
    typeId: 'P100',
    parse: (val: any) => val && numeral(val).format('0.00'),
    parseForExport: (val: any) => val && precisionRound(val, 2),
    format: (val: any, options: any) => {
        const { showFraction = true } = options || {};
        return val != null ? numeral(val / 100).format(`0,0${showFraction ? '.00' : ''} %`) : '-';
    },
    formatTitle: identity,
};

const DURATION_SECONDS: TypeDefinition = {
    typeId: 'DURATION_SECONDS',
    parse: identity,
    parseForExport: (val: any) => DURATION_SECONDS.format(val),
    format: (val: any) => {
        const padInt = (number: any) => number.toString().padStart(2, '0');
        const format = (value: any) => {
            const duration = moment.duration(Math.floor(parseFloat(value)), 'seconds');

            const seconds = padInt(duration.seconds());
            const minutes = padInt(duration.minutes());
            const hours = padInt(Math.floor(duration.asHours()));

            return `${hours}h ${minutes}m ${seconds}s`;
        };

        return val != null ? format(val) : '-';
    },
    formatTitle: identity,
};

const WEEKDAY: TypeDefinition = {
    typeId: 'WEEKDAY',
    parse: identity,
    parseForExport: (val: any) => WEEKDAY.format(val),
    format: (val: any) => {
        const toWeekdayString = (num: any) => {
            const weekday = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            return weekday[num - 1];
        };
        const isValidWeekday = Number.isInteger(val) && val >= 1 && val <= 7;
        return isValidWeekday ? toWeekdayString(val) : '-';
    },
    formatTitle: identity,
};

export const DATA_TYPES: { [key: string]: TypeDefinition } = Object.freeze({
    DATE,
    DATE_TIME,
    WEEKDAY,
    TEXT,
    INT,
    ID,
    FLOAT,
    EUR,
    EUR4,
    EUR_SYMBOL,
    P100,
    RATIO,
    DURATION_SECONDS,
});

export const typeOf = (typeId: string): TypeDefinition => {
    const resolvedType = Object.keys(DATA_TYPES)
        .map(key => DATA_TYPES[key])
        .find(type => type.typeId === typeId);
    return resolvedType || TEXT;
};
